// ============================================
// FILE: src/utils/imageHelpers.js
// ============================================

const getBackendOrigin = () => {
  const apiUrl = process.env.REACT_APP_ASSETS_BASE_URL || 'https://panel.dealstore.app';

  try {
    if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      return apiUrl.origin;
    }

    if (typeof window !== 'undefined') {
      return new URL(apiUrl, window.location.origin).origin;
    }
  } catch {
    // Fall back to localhost for malformed or missing URL config.
  }

  return 'https://panel.dealstore.app';
};

export const resolveProductImageUrl = (rawImageUrl) => {
  if (!rawImageUrl || typeof rawImageUrl !== 'string') {
    return null;
  }

  const imageUrl = rawImageUrl.trim();
  if (!imageUrl || imageUrl === 'null' || imageUrl.includes('�')) {
    return null;
  }

  if (/^(https?:)?\/\//i.test(imageUrl)) {
    return imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
  }

  if (/^(data:|blob:)/i.test(imageUrl)) {
    return imageUrl;
  }

  const backendOrigin = getBackendOrigin();
  console.log("product image url ::"+backendOrigin);
  if (imageUrl.startsWith('/')) {
    return `${backendOrigin}${imageUrl}`;
  }

  if (imageUrl.startsWith('uploads/') || imageUrl.startsWith('storage/')) {
    return `${backendOrigin}/${imageUrl}`;
  }

  return imageUrl;
};

/**
 * Default product image — shown whenever a product has no image, or its image
 * fails to load (404, dead host, CORS). Same asset the mobile app bundles, so
 * every storefront shows the same stand-in instead of a per-screen emoji.
 */
export const BLANK_PRODUCT_IMAGE = `${process.env.PUBLIC_URL || ''}/blank_image.jpeg`;

/**
 * onError handler for product <img> tags: swap in the blank image once. The
 * guard stops a loop if the blank image itself can't be fetched.
 */
export const handleProductImageError = (e) => {
  const img = e.currentTarget || e.target;
  if (!img || img.dataset.blankFallback) return;
  img.dataset.blankFallback = '1';
  img.src = BLANK_PRODUCT_IMAGE;
};

/** A product's image URL, or the blank image when it has none. */
export const getProductImageUrl = (product) => {
  // Check all known backend/frontend image fields in priority order.
  const rawImageUrl =
    product?.featured_image_url ||
    product?.image_url ||
    product?.featured_image ||
    product?.product_image ||
    product?.thumbnail_url ||
    product?.thumbnail ||
    product?.image;

  return resolveProductImageUrl(rawImageUrl) || BLANK_PRODUCT_IMAGE;
};

const PRODUCT_IMAGE_SIZE_CLASSES = {
  small: 'w-8 h-8',
  medium: 'w-12 h-12',
  large: 'w-16 h-16',
  xl: 'w-20 h-20',
  modal: 'w-full h-64'
};

/**
 * Shared product image element. Falls back to the blank image both when the
 * product has no image and when its image fails to load.
 * Used across HomeScreen, ProductDetailsModal, OrdersScreen, and OrderConfirmationModal
 */
export const getProductImage = (product, size = 'medium') => {
  const src = getProductImageUrl(product);
  const alt = product?.name || product?.product_name || 'Product';

  if (size === 'card') {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover bg-gray-50 transition-transform duration-300 group-hover:scale-110"
        onError={handleProductImageError}
      />
    );
  }

  if (size === 'modal') {
    return (
      <div className="relative w-full h-72 sm:h-80 bg-gray-50 rounded-xl overflow-hidden flex items-center justify-center">
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full w-auto h-auto object-contain"
          onError={handleProductImageError}
        />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`${PRODUCT_IMAGE_SIZE_CLASSES[size] || PRODUCT_IMAGE_SIZE_CLASSES.medium} object-cover rounded-lg bg-gray-100`}
      onError={handleProductImageError}
    />
  );
};

/**
 * Kept for existing callers: the "no image" placeholder is now the blank
 * product image rather than an emoji.
 */
export const getProductEmoji = (product, size = 'medium') => getProductImage(
  { name: product?.name || product?.product_name },
  size
);

/**
 * Get product image for order items (handles different data structures)
 */
export const getOrderItemImage = (item, size = 'medium') => {
  // Try to construct a product object from order item
  const product = {
    name: item.name || item.product_name || item.product?.name,
    featured_image_url: item.featured_image_url || item.product?.featured_image_url || item.product_image_url || item.product?.product_image_url,
    image_url: item.image_url || item.product?.image_url || item.product_image,
    featured_image: item.featured_image || item.product?.featured_image,
    product_image: item.product_image || item.product?.product_image,
    thumbnail_url: item.thumbnail_url || item.product?.thumbnail_url,
    thumbnail: item.thumbnail || item.product?.thumbnail,
    image: item.image || item.product?.image
  };
  
  return getProductImage(product, size);
};